/**
 * RulesTabContent Component
 *
 * Rules management content for the Audit Center rules tab.
 * Reuses the same components and logic from RulesManagementPage.
 * AI chat integration is handled via navigation to Rule Build page.
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateTaskFromPromptMutation,
  useCreateTaskFromRuleMutation,
} from '@features/rule-build';
import { DeleteRuleDialog, RuleDetailPanel, RuleFormPanel } from '@features/rules/components';
import {
  useDeleteRuleMutation,
  useGetRuleQuery,
} from '@features/rules/store/api';
import type { ProviderRuleResponse } from '@features/rules/types';
import { Box, CircularProgress, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';

import { RuleComplianceOverview } from '../dashboard/RuleComplianceOverview';

import { AddRuleCard } from './AddRuleCard';
import { RulesAnalyticsFilters, type RulesAnalyticsFilters as RulesAnalyticsFiltersType } from './RulesAnalyticsFilters';
import { RulesIntroductionCard } from './RulesIntroductionCard';

interface RulesTabContentProps {
  /** Organization ID for admin mode */
  organizationId?: string;
}

export const RulesTabContent: React.FC<RulesTabContentProps> = ({ organizationId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Selection state for detail drawer - store ID and fetch via query for auto-refresh on invalidation
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);

  // Panel mode: 'view' | 'create' | 'edit'
  const [panelMode, setPanelMode] = useState<'view' | 'create' | 'edit'>('view');

  // Fetch selected rule data via query - auto-refreshes when Rule tag is invalidated
  const { data: selectedRule } = useGetRuleQuery(selectedRuleId ?? '', { skip: !selectedRuleId });

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<ProviderRuleResponse | null>(null);

  // Delete mutation
  const [deleteRule, { isLoading: isDeleting }] = useDeleteRuleMutation();

  // Rule Build mutations
  // Note: Loading states available for future UI enhancements
  const [createFromPrompt, { isLoading: _isCreatingFromPrompt }] = useCreateTaskFromPromptMutation();
  const [createFromRule, { isLoading: _isCreatingFromRule }] = useCreateTaskFromRuleMutation();

  // Analytics filters state
  const [analyticsFilters, setAnalyticsFilters] = useState<RulesAnalyticsFiltersType>({
    comparisonMode: false,
  });

  // Ref for scrolling to Add Rule Card
  const addRuleCardRef = React.useRef<HTMLDivElement>(null);

  // Handle scroll to Add Rule Card from introduction card
  const handleScrollToAddRule = useCallback(() => {
    addRuleCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  // Handle create new rule from popover - creates task from prompt and navigates to Rule Build
  const handleCreateRule = useCallback(
    async (description: string) => {
      try {
        const result = await createFromPrompt({
          fromPromptRequest: { prompt: description },
        }).unwrap();
        navigate(`/rule-build/${result.task_id}`);
      } catch (error) {
        console.error('Failed to create rule from prompt:', error);
        enqueueSnackbar('Failed to create rule. Please try again.', { variant: 'error' });
      }
    },
    [createFromPrompt, navigate, enqueueSnackbar],
  );

  // Handle edit rule with AI - creates task from existing rule and navigates to Rule Build
  const handleEditRuleWithAI = useCallback(
    async (ruleId: string) => {
      try {
        const result = await createFromRule({ ruleId }).unwrap();
        navigate(`/rule-build/${result.task_id}`);
      } catch (error) {
        console.error('Failed to load rule for editing:', error);
        enqueueSnackbar('Failed to load rule for editing. Please try again.', { variant: 'error' });
      }
    },
    [createFromRule, navigate, enqueueSnackbar],
  );

  const handlePanelClose = useCallback(() => {
    setSelectedRuleId(null);
    setPanelMode('view');
  }, []);

  const handleEditRule = useCallback(() => {
    if (selectedRuleId) {
      setPanelMode('edit');
    }
  }, [selectedRuleId]);

  const handleDeleteRule = useCallback(() => {
    if (selectedRule) {
      setRuleToDelete(selectedRule);
      setDeleteDialogOpen(true);
    }
  }, [selectedRule]);

  const handleConfirmDelete = useCallback(async () => {
    if (!ruleToDelete) return;

    try {
      await deleteRule({
        ruleId: ruleToDelete.rule_id,
        ruleType: ruleToDelete.rule_type,
      }).unwrap();
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
      setSelectedRuleId(null);
      setPanelMode('view');
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  }, [ruleToDelete, deleteRule]);

  const handleDeleteDialogClose = useCallback(() => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  }, [isDeleting]);

  const handleFormSuccess = useCallback(() => {
    if (panelMode === 'create') {
      setSelectedRuleId(null);
      setPanelMode('view');
    } else if (panelMode === 'edit' && selectedRuleId) {
      setPanelMode('view');
    }
  }, [panelMode, selectedRuleId]);

  return (
    <Box sx={{ pb: 4 }}>
      {/* Rules Introduction Card */}
      <RulesIntroductionCard onTryItOut={handleScrollToAddRule} />

      {/* Analytics Filters */}
      <RulesAnalyticsFilters
        filters={analyticsFilters}
        onFiltersChange={setAnalyticsFilters}
      />

      {/* Rule Compliance Overview Dashboard with Add Rule Card */}
      <RuleComplianceOverview
        leftmostCard={
          <Box ref={addRuleCardRef}>
            <AddRuleCard onCreateRule={handleCreateRule} />
          </Box>
        }
        filters={analyticsFilters}
        organizationId={organizationId}
        onEditRule={handleEditRuleWithAI}
      />

      {/* Detail/Form Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedRuleId || panelMode === 'create' || panelMode === 'edit'}
        onClose={handlePanelClose}
        PaperProps={{
          sx: { width: isMobile ? '100%' : 600, p: 0 },
        }}
      >
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
          {panelMode === 'view' && selectedRuleId ? (
            selectedRule ? (
              <RuleDetailPanel
                rule={selectedRule}
                onClose={handlePanelClose}
                onEdit={handleEditRule}
                onDelete={handleDeleteRule}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            )
          ) : panelMode === 'create' || panelMode === 'edit' ? (
            <RuleFormPanel
              mode={panelMode}
              rule={selectedRule ?? null}
              onClose={handlePanelClose}
              onSuccess={handleFormSuccess}
            />
          ) : null}
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <DeleteRuleDialog
        open={deleteDialogOpen}
        rule={ruleToDelete}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </Box>
  );
};
